import { ANPREvent } from 'domain/anpr-event'
import { useAuth } from 'layout/context/auth-context'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useState } from 'react'
import { usePlateHistory } from '../hooks/usePlateHistory'
import { emptyANPREvent } from '../types/consts'
import { exportANPREventsToCSV } from '../util/export-csv'
import ANPREventListItem from './anpr-event-list-item'
import EventDetailViewer from './anpr-event-detail-dialog'
import EventImageViewer from './anpr-event-image-dialog'
import './plate-history-view.scss'

const PlateHistoryView = () => {
  const { clientId } = useAuth()
  const { events, isLoading, error, searched, fetchPlateHistory } = usePlateHistory()

  const [licensePlate, setLicensePlate] = useState('')
  const [anprEvent, setANPREvent] = useState<ANPREvent>(emptyANPREvent)
  const [eventDialog, setEventDialog] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const handleSearch = () => {
    if (clientId) {
      fetchPlateHistory(clientId, licensePlate)
    }
  }

  const viewEventDetails = (event: ANPREvent) => {
    setANPREvent({ ...event })
    setEventDialog(true)
  }

  const exportEvents = () => exportANPREventsToCSV(events)

  const firstSeen = events.length > 0 ? events[events.length - 1].createdDate : null
  const lastSeen = events.length > 0 ? events[0].createdDate : null

  return (
    <div className='plate-history-container'>
      <div className='plate-history-header'>
        <h2 className='plate-history-title'>
          <i className='pi pi-search' />
          Histórico de Placa
        </h2>
        <p className='plate-history-subtitle'>
          Busque uma placa para ver todas as passagens registradas, por câmera,
          com imagens — sem limite de período.
        </p>
      </div>

      <Card className='plate-history-search-card'>
        <div className='plate-history-search-row'>
          <InputText
            value={licensePlate}
            onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder='Digite a placa (ex.: ABC1D23)'
            className='plate-history-search-input'
            maxLength={10}
          />
          <Button
            label='Buscar'
            icon='pi pi-search'
            onClick={handleSearch}
            disabled={!licensePlate.trim() || isLoading}
          />
        </div>
      </Card>

      {isLoading && (
        <div
          className='plate-history-state'
          data-testid='plate-history-loading'
        >
          <ProgressSpinner strokeWidth='4' />
        </div>
      )}

      {!isLoading && error && (
        <div
          className='plate-history-state plate-history-state--error'
          data-testid='plate-history-error'
        >
          <i className='pi pi-exclamation-triangle' />
          <span>{error}</span>
        </div>
      )}

      {!isLoading && !error && searched && events.length === 0 && (
        <div
          className='plate-history-state'
          data-testid='plate-history-empty'
        >
          <i className='pi pi-info-circle' />
          <span>Nenhuma passagem registrada para esta placa.</span>
        </div>
      )}

      {!isLoading && !error && events.length > 0 && (
        <>
          <div className='plate-history-summary'>
            <span className='plate-history-summary-chip'>
              <i className='pi pi-list' />
              {events.length} {events.length === 1 ? 'passagem' : 'passagens'}
            </span>
            {firstSeen && (
              <span className='plate-history-summary-chip'>
                <i className='pi pi-calendar-plus' />
                Primeira vez: {new Date(firstSeen).toLocaleString('pt-BR')}
              </span>
            )}
            {lastSeen && (
              <span className='plate-history-summary-chip'>
                <i className='pi pi-clock' />
                Última vez: {new Date(lastSeen).toLocaleString('pt-BR')}
              </span>
            )}
            <Button
              label='Exportar CSV'
              icon='pi pi-download'
              outlined
              size='small'
              onClick={exportEvents}
            />
          </div>

          <div className='grid'>
            {events.map((event, index) => (
              <ANPREventListItem
                key={`${event.createdDate}-${event.cameraName}-${index}`}
                event={event}
                onViewDetails={viewEventDetails}
                onImageClick={setSelectedImage}
                onExport={exportEvents}
              />
            ))}
          </div>
        </>
      )}

      <EventDetailViewer
        setSelectedImage={setSelectedImage}
        anprEvent={anprEvent}
        hideDialog={() => setEventDialog(false)}
        visible={eventDialog}
      />
      <EventImageViewer selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
    </div>
  )
}

export default PlateHistoryView
